import React, { FC, useState } from 'react';
import * as Tabs from '@radix-ui/react-tabs';
import { Field, Image, ImageField, withDatasourceCheck } from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';

import { ComponentProps, isColorBright, resolveColorProperty } from '@gn-audio/jss-components';

type MediaTab = {
  id: string;
  TabText: Field<string>;
  TabImage: ImageField;
};

type MediaTabsFields = {
  ImageBackgroundColor: Field<string>;
  TabSelectorBackgroundColor: Field<string>;
  SelectedTabBackgroundColor: Field<string>;
  BackgroundColor: Field<string>;
  RoundedImageCorners: Field<boolean>;
  SwipeAnimation: Field<string>;
  Tabs: MediaTab[];
};

type MediaTabsProps = {
  fields: MediaTabsFields;
} & ComponentProps;

const generateColorScheme = (
  tabSelectorBackground: string,
  selectedTabBackground: string,
  imageBackgroundColor: string
): React.CSSProperties => {
  const tabColor = resolveColorProperty(tabSelectorBackground);
  const selectedColor = resolveColorProperty(selectedTabBackground);
  const imageColor = resolveColorProperty(imageBackgroundColor);

  return {
    '--tab-text-color': isColorBright(tabColor) ? 'black' : 'white',
    '--active-text-color': isColorBright(selectedColor) ? 'black' : 'white',
    '--tab-bg-color': tabColor,
    '--active-bg-color': selectedColor,
    '--image-bg-color': imageColor,
  } as React.CSSProperties;
};

const MediaTabs: FC<MediaTabsProps> = ({ fields }) => {
  const mediaTabsFields = fields;
  const [activeTab, setActiveTab] = useState(mediaTabsFields.Tabs[0].id);

  const bgColor = mediaTabsFields.BackgroundColor?.value;
  const isBackgroundColorGradient = (BackgroundColor: string | undefined) => {
    return BackgroundColor?.startsWith('linear-gradient');
  };
  const spotBackgroundColor = isBackgroundColorGradient(bgColor)
    ? bgColor
    : (resolveColorProperty(bgColor) ?? 'transparent');
  const spotBackgroundStyle = {
    background: `${spotBackgroundColor}`,
  };

  const getTransitionStyles = (tabIndex: number) => {
    if (!mediaTabsFields.SwipeAnimation?.value) {
      return {
        opacity: tabIndex === mediaTabsFields.Tabs.findIndex((tab) => tab.id === activeTab) ? 1 : 0,
        zIndex: tabIndex === mediaTabsFields.Tabs.findIndex((tab) => tab.id === activeTab) ? 1 : 0,
      };
    }

    const activeTabIndex = mediaTabsFields.Tabs.findIndex((tab) => tab.id === activeTab);
    if (tabIndex === activeTabIndex) {
      return {
        clipPath: 'inset(0%)',
      };
    }

    return tabIndex < activeTabIndex
      ? {
          clipPath: 'inset(0%)',
        }
      : {
          clipPath: 'inset(0% 100% 0% 0%)',
        };
  };

  const colorScheme = React.useMemo(
    () =>
      generateColorScheme(
        mediaTabsFields.TabSelectorBackgroundColor?.value || '',
        mediaTabsFields.SelectedTabBackgroundColor?.value || '',
        mediaTabsFields.ImageBackgroundColor?.value || ''
      ),
    [
      mediaTabsFields.TabSelectorBackgroundColor,
      mediaTabsFields.SelectedTabBackgroundColor,
      mediaTabsFields.ImageBackgroundColor,
    ]
  );

  return (
    <div className="container-fluid" style={spotBackgroundStyle}>
      <div className="container !py-0">
        <Tabs.Root
          style={colorScheme}
          className="flex flex-col items-center justify-center"
          defaultValue={mediaTabsFields.Tabs[0].id}
          onValueChange={setActiveTab}
        >
          <div className="relative">
            {mediaTabsFields.Tabs?.map((tab, index) => (
              <Tabs.Content
                forceMount
                className={classNames(
                  index > 0 ? 'tabContent absolute left-0 top-0' : '',
                  'transition-clip h-full w-full duration-300 ease-in-out',
                  'flex items-center justify-center',
                  mediaTabsFields.RoundedImageCorners?.value ? 'rounded-lg' : '',
                  'overflow-hidden'
                )}
                style={{
                  ...getTransitionStyles(index),
                  transition: 'opacity 0.3s ease-in-out, clip-path 0.3s ease-in-out',
                }}
                key={tab.id}
                value={tab.id}
              >
                <Image
                  className={classNames(
                    'relative z-10 h-full w-full object-contain',
                    mediaTabsFields.RoundedImageCorners?.value ? 'rounded-lg' : ''
                  )}
                  field={tab.TabImage.value}
                  loading="lazy"
                />
                {mediaTabsFields.ImageBackgroundColor?.value && (
                  <div className="absolute inset-12 z-0 rounded-full bg-[var(--image-bg-color)]" />
                )}
              </Tabs.Content>
            ))}
          </div>
          <Tabs.List className="mt-4 flex items-stretch gap-3 rounded-full border-4 border-black/5 bg-[var(--tab-bg-color)]">
            {mediaTabsFields.Tabs?.map((tab) => (
              <Tabs.Trigger
                key={tab.id}
                value={tab.id}
                className={`text-md flex flex-1 basis-0 items-center justify-center rounded-full px-3 py-1.5 data-[state=active]:bg-[var(--active-bg-color)] data-[state=active]:text-[--active-text-color] data-[state=inactive]:text-[var(--tab-text-color)] md:px-10 md:py-3 md:text-xl`}
              >
                {tab.TabText.value}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs.Root>
      </div>
    </div>
  );
};

export default withDatasourceCheck()(MediaTabs);
